import { darken, lighten } from '@mui/material';
import { grey } from '@mui/material/colors';

const PRIMARY = '#FA4B00';
const SECONDARY = '#212121';
const SUCCESS = '#4CAF50';
const ERROR = '#D32F2F';
const WARNING = '#FFA726';
const INFO = '#2196F3';
const LIGHT = {
    text: '#2B2B2B',
    disabledText: '#CECECE',
    altText: '#A2A2A2',
    background: '#F9FAFB',
    mainHeader: 'rgb(244, 244, 244)',
    childHeader: '#F4F4F4',
    disabledBackground: '#F2F2F2',
    fieldBorder: '#D2D2D2',
    tableBorder: '#E2E2E2',
    icon: '#546178',
    quaternary: '#546178',
    surface: '#FFFFFF',
    surfaceSecondary: '#F0F3F9'
};

const DARK = {
    text: '#E7EAF0',
    disabledText: '#7D8596',
    altText: '#9AA3B2',
    background: '#111111',
    mainHeader: '#181818',
    childHeader: '#1E1E1E',
    disabledBackground: '#161616',
    fieldBorder: '#3A3A3A',
    tableBorder: '#2A2A2A',
    icon: '#9AA3B2',
    quaternary: '#9AA3B2',
    surface: '#181818',
    surfaceSecondary: '#202020'
};

const createTone = (mode) => (mode === 'dark' ? DARK : LIGHT);

const createPalette = (mode = 'light') => {
    const tone = createTone(mode);

    return {
        mode,
        primary: {
            light: lighten(PRIMARY, 0.4),
            main: PRIMARY,
            dark: darken(PRIMARY, 0.1),
            alert: mode === 'dark' ? 'rgba(250, 75, 0, 0.18)' : '#d1eef8'
        },
        secondary: {
            light: lighten(SECONDARY, 0.6),
            main: SECONDARY,
            dark: darken(SECONDARY, 0.1)
        },
        tertiary: {
            light: lighten(tone.altText, 0.6),
            main: tone.altText,
            dark: darken(tone.altText, 0.1)
        },
        quaternary: {
            light: lighten(tone.quaternary, 0.6),
            main: tone.quaternary,
            dark: darken(tone.quaternary, 0.1)
        },
        success: {
            light: lighten(SUCCESS, 0.4),
            main: SUCCESS,
            dark: darken(SUCCESS, 0.1),
            alert: mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : '#beede1',

            // Legacy
            primary: '#5cd3b4',
            hover: lighten('#5cd3b4', 0.2)
        },
        warning: {
            light: lighten(WARNING, 0.4),
            main: WARNING,
            dark: darken(WARNING, 0.1)
        },
        error: {
            light: lighten(ERROR, 0.4),
            main: ERROR,
            dark: darken(ERROR, 0.1),
            alert: mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffe2e1'
        },
        fail: {
            light: lighten(ERROR, 0.4),
            main: ERROR,
            dark: darken(ERROR, 0.1),
            alert: mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : '#ffe2e1'
        },
        info: {
            light: lighten(INFO, 0.7),
            main: INFO,
            dark: darken(INFO, 0.1)
        },
        text: {
            main: tone.text,
            dark: darken(tone.text, 0.1),
            secondary: tone.altText,
            disabled: tone.disabledText,
            success: '#FFFFFF'
        },
        background: {
            mainHeader: tone.mainHeader,
            childHeader: tone.childHeader,
            dark: darken(tone.background, 0.1),
            default: tone.background,
            disabled: tone.disabledBackground,
            secondary: tone.surfaceSecondary,
            white: tone.surface
        },
        border: {
            light: lighten(tone.fieldBorder, 0.7),
            main: tone.fieldBorder,
            dark: darken(tone.fieldBorder, 0.1),
            secondary: tone.tableBorder
        },

        // Legacy
        common: {
            transparent: 'rgba(0, 0, 0, 0)'
        },
        icon: {
            light: lighten(tone.icon, 0.7),
            main: tone.icon,
            dark: darken(tone.icon, 0.1),
            opacity: {
                regular: 0.5,
                hover: 1
            }
        },

        amend: {
            main: mode === 'dark' ? 'rgba(209, 238, 248, 0.15)' : '#d1eef8',
            dark: lighten('#d1eef8', 0.2)
        },
        cancel: {
            main: grey[800]
        },

        db_overview_1: {
            main: 'rgb(250, 75, 0)',
            dark: 'rgba(250, 75, 0, 1)',
            light: mode === 'dark' ? 'rgba(250, 75, 0, 0.1)' : 'rgba(255, 237, 230, 1)'
        },
        db_overview_2: {
            main: 'rgb(108, 233, 116)',
            dark: 'rgb(40, 198, 46)',
            light: mode === 'dark' ? 'rgba(76, 175, 80, 0.16)' : 'rgba(237, 247, 238, 1)'
        },
        db_overview_3: {
            main: 'rgb(255, 118, 118)',
            dark: 'rgb(248, 54, 54)',
            light: mode === 'dark' ? 'rgba(248, 54, 54, 0.07)' : 'rgba(255, 219, 209, 1)'
        },
        db_overview_4: {
            main: 'rgba(255, 220, 168, 1)',
            dark: 'rgba(255, 167, 38, 1)',
            light: mode === 'dark' ? 'rgba(255, 167, 38, 0.16)' : 'rgba(255, 246, 233, 1)'
        },
        db_overview_5: {
            main: 'rgba(216, 197, 141, 1)',
            dark: 'rgba(180, 155, 52, 1)',
            light: mode === 'dark' ? 'rgba(180, 155, 52, 0.16)' : 'rgba(255, 250, 229, 1)'
        },
        db_overview_6: {
            main: 'rgba(122, 192, 248, 1)',
            dark: 'rgba(33, 150, 243, 1)',
            light: mode === 'dark' ? 'rgba(33, 150, 243, 0.16)' : 'rgba(233, 245, 254, 1)'
        },
        db_overview_7: {
            main: 'rgba(136, 151, 174, 1)',
            dark: 'rgb(35, 85, 161)',
            light: mode === 'dark' ? 'rgba(94, 113, 141, 0.16)' : 'rgba(240, 243, 249, 1)'
        },
        approved: {
            main: 'rgba(76, 175, 80, 1)',
            dark: 'rgba(76, 175, 80, 1)',
            light: mode === 'dark' ? 'rgba(76, 175, 80, 0.16)' : 'rgba(232, 245, 233, 1)'
        },
        pending: {
            main: 'rgba(255, 167, 38, 1)',
            dark: 'rgba(255, 167, 38, 1)',
            light: mode === 'dark' ? 'rgba(255, 167, 38, 0.16)' : 'rgba(255, 243, 224, 1)'
        },
        rejected: {
            main: 'rgba(255, 87, 34, 1)',
            dark: 'rgba(255, 87, 34, 1)',
            light: mode === 'dark' ? 'rgba(255, 87, 34, 0.16)' : 'rgba(255, 235, 238, 1)'
        },
        cancelled: {
            main: 'rgba(158, 158, 158, 1)',
            dark: 'rgba(158, 158, 158, 1)',
            light: mode === 'dark' ? 'rgba(158, 158, 158, 0.16)' : 'rgba(224, 224, 224, 1)'
        }
    };
};

export { createPalette };
export default createPalette;
